import type {
  AcroFieldValue,
  FillerErrorCode,
  InspectResult,
} from '@/features/acroform-filler/model/types'

interface WorkerSuccess<T> {
  id: number
  ok: true
  result: T
}

interface WorkerFailure {
  id: number
  ok: false
  code: FillerErrorCode
}

export class AcroFormWorkerClient {
  private active: { worker: Worker; reject: (reason: DOMException) => void } | null = null
  private requestId = 0

  cancel(): void {
    this.requestId += 1
    const active = this.active
    this.active = null
    if (active) {
      active.worker.terminate()
      active.reject(new DOMException('The PDF operation was cancelled', 'AbortError'))
    }
  }

  private run<T>(payload: object, bytes: ArrayBuffer): Promise<T> {
    this.cancel()
    const id = this.requestId
    const worker = new Worker(new URL('./filler.worker.ts', import.meta.url), { type: 'module' })
    return new Promise<T>((resolve, reject) => {
      this.active = { worker, reject }
      worker.onmessage = (event: MessageEvent<WorkerSuccess<T> | WorkerFailure>) => {
        if (event.data.id !== id || worker !== this.active?.worker) return
        worker.terminate()
        this.active = null
        if (event.data.ok) resolve(event.data.result)
        else reject(event.data.code)
      }
      worker.onerror = () => {
        if (worker !== this.active?.worker) return
        worker.terminate()
        this.active = null
        reject('processing' satisfies FillerErrorCode)
      }
      worker.postMessage({ ...payload, id, bytes }, [bytes])
    })
  }

  inspect(bytes: ArrayBuffer): Promise<InspectResult> {
    return this.run<InspectResult>({ kind: 'inspect' }, bytes)
  }

  fill(
    bytes: ArrayBuffer,
    values: Record<string, AcroFieldValue>,
    flatten: boolean,
  ): Promise<ArrayBuffer> {
    return this.run<ArrayBuffer>({ kind: 'fill', values, flatten }, bytes)
  }
}
